import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Billing from "@/pages/Billing";
import Inventory from "@/pages/Inventory";
import Menu from "@/pages/Menu";
import Orders from "@/pages/Orders";
import Kitchen from "@/pages/Kitchen";
import Customers from "@/pages/Customers";
import Suppliers from "@/pages/Suppliers";
import Expenses from "@/pages/Expenses";
import CashFlow from "@/pages/CashFlow";
import Marketing from "@/pages/Marketing";
import AIAdvisor from "@/pages/AIAdvisor";
import Reports from "@/pages/Reports";
import Forecast from "@/pages/Forecast";
import Alerts from "@/pages/Alerts";
import Branches from "@/pages/Branches";
import Team from "@/pages/Team";
import OpenShop from "@/pages/OpenShop";
import Subscription from "@/pages/Subscription";
import FounderDashboard from "@/pages/FounderDashboard";

function FullLoader() {
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "founder") return <Navigate to="/founder" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function FounderOnly() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "founder" && !user.is_super_admin) return <Navigate to="/dashboard" replace />;
  return <FounderDashboard />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/founder" element={<FounderOnly />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/open-shop" element={<Protected roles={["owner", "manager"]}><OpenShop /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/inventory" element={<Protected roles={["owner", "manager"]}><Inventory /></Protected>} />
      <Route path="/menu" element={<Protected roles={["owner", "manager"]}><Menu /></Protected>} />
      <Route path="/orders" element={<Protected><Orders /></Protected>} />
      <Route path="/kitchen" element={<Protected><Kitchen /></Protected>} />
      <Route path="/customers" element={<Protected roles={["owner", "manager"]}><Customers /></Protected>} />
      <Route path="/suppliers" element={<Protected roles={["owner", "manager"]}><Suppliers /></Protected>} />
      <Route path="/expenses" element={<Protected roles={["owner", "manager"]}><Expenses /></Protected>} />
      <Route path="/cashflow" element={<Protected roles={["owner"]}><CashFlow /></Protected>} />
      <Route path="/marketing" element={<Protected roles={["owner"]}><Marketing /></Protected>} />
      <Route path="/advisor" element={<Protected roles={["owner", "manager"]}><AIAdvisor /></Protected>} />
      <Route path="/reports" element={<Protected roles={["owner", "manager"]}><Reports /></Protected>} />
      <Route path="/forecast" element={<Protected roles={["owner"]}><Forecast /></Protected>} />
      <Route path="/alerts" element={<Protected roles={["owner", "manager"]}><Alerts /></Protected>} />
      <Route path="/branches" element={<Protected roles={["owner"]}><Branches /></Protected>} />
      <Route path="/team" element={<Protected roles={["owner"]}><Team /></Protected>} />
      <Route path="/subscription" element={<Protected roles={["owner"]}><Subscription /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}
